import { applyUsableFilters, convertStringToArray, generateErrors, isFeatureEnabled, applyTransportPlanLegVoyages, withAppliedFilters } from 'utilities/util';
import { ThunkDispatch } from 'redux-thunk';
import { IStore } from 'reducers/index';
import { Action } from 'redux';
import { TYPES as FILTER_TYPES } from 'action-types/filters';
import { TYPES } from 'action-types/exportJob';
import { displayFilters } from 'actions/filters';
import { callApiPost, callApiV2Get, callApiV2Post, callApiV3Get, callSettingsApiGet } from 'utilities/apiCaller';
import API from 'constants/api';
import { TransportMode } from 'constants/modes';
import { FEATURE_TOGGLES } from 'constants/featureToggles';

export const fetchNftpBillable = (jobId: number) => {
    return async (dispatch: ThunkDispatch<any, any, Action>) => {
        dispatch({ type: TYPES.FETCH_NFTP_BILLABLE.PENDING });
        try {
            const res = await callApiV2Get(
                API.ExportJob.NftpRouter,
                { jobId, messageType: 'SalesOrder' },
                {},
                true,
            );
            dispatch({ type: TYPES.FETCH_NFTP_BILLABLE.FULFILLED, nftpBillableResponse: res.data });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_NFTP_BILLABLE.REJECTED, errors });
        }
    };
};

export const fetchExportJobs = (pageNumber?: number, pageSize?: number, genericFilter?: string) => {
    return async (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
        dispatch({ type: TYPES.FETCH_EXPORT_JOB.PENDING });
        displayFilters(false, 'jobCostingFiltersExportJob')
        try {
            const payload = applyUsableFilters(convertStringToArray(getState, 'documentNumbers'), {
                        Page: pageNumber,
                        PageSize: pageSize
                    });
            const res = await callApiV2Post(API.ExportJob.ExportJobSearch, payload, {});
            dispatch({ type: FILTER_TYPES.SET_TOTAL_COUNT, totalCount: res.data.totalCount });
            // remember the exact request that produced the displayed results so
            // bulk "download all results" collection can replay it page by page
            dispatch({ type: FILTER_TYPES.SET_LAST_SEARCH_PAYLOAD, target: 'exportJobs', payload });
            dispatch({ type: TYPES.FETCH_EXPORT_JOB.FULFILLED, exportJobs: res.data });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_EXPORT_JOB.REJECTED, errors });
        }
    }
};

export const resetExportJobs = () => {
    return async (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
        dispatch({ type: TYPES.RESET_EXPORT_JOB.PENDING });
        dispatch({ type: FILTER_TYPES.SET_TOTAL_COUNT, totalCount: 0 });
        dispatch({ type: TYPES.RESET_EXPORT_JOB.FULFILLED, exportJobs: [] });

    }
};

/**
 * Fetches a single search page for the "download all results" flow.
 * Replays the exact payload of the last successful grid search (captured in
 * filters.lastSearchPayloads, covering Apply, pagination, and sorting), so
 * collection always matches the displayed results and count. When no payload
 * was captured yet, falls back to the last applied filter snapshot so that
 * edited-but-unapplied inputs still cannot leak into the query. Runs in the
 * background so collection keeps global loading and error indicators out of
 * the singleton store. Returns minimal row objects carrying only what the
 * bulk download action needs.
 */
export const fetchExportJobsPageForDownload = (pageNumber: number, pageSize: number) => {
    return async (_dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
        const filtersState = getState().filters || ({} as any);
        const captured = (filtersState.lastSearchPayloads || {}).exportJobs;
        const payload = captured
            ? { ...captured, Page: pageNumber, PageSize: pageSize }
            : applyUsableFilters(convertStringToArray(withAppliedFilters(getState), 'documentNumbers'), {
                Page: pageNumber,
                PageSize: pageSize
            });
        const res = await callApiV2Post(API.ExportJob.ExportJobSearch, payload, {}, true);
        return ((res.data && res.data.result) || []).map((item: any) => ({
            exportJobId: item.exportJobId
        }));
    }
};

export const resetExportJobsDetails = () => {
    return async (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
        dispatch({ type: TYPES.RESET_EXPORT_JOB_DETAILS });
    }
};

export const fetchExportJobDropdown = () => {
    return async (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
        const isMciChargeEnabled = isFeatureEnabled(FEATURE_TOGGLES.EXPORT_MCI_CHARGE_ENABLED, getState().featureToggle.featureToggles);
        dispatch({ type: TYPES.FETCH_EXPORT_JOB_DROPDOWN.PENDING });
        try {
            const page = 1;
            const limit = 1000;

            const res = await callSettingsApiGet(`${API.FinanceConfig.DocumentStatus}`, {page, limit});

            const dropdownData = res.data.data || [];
            if (!dropdownData.some((item: any) => item.documentType === 'FRN') && isMciChargeEnabled) {
                dropdownData.push({ documentType: 'FRN', documentStatusName: [] });
            }
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DROPDOWN.FULFILLED, documentDropdownData: dropdownData});
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DROPDOWN.REJECTED, errors });
        }
    }
};

export const fetchExportJobDetails = (exportJobId: number) => {
    return async (dispatch: ThunkDispatch<any, any, Action>) => {
        dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS.PENDING });
        try {
            const res = await callApiV2Get(`${API.ExportJob.ExportJobDetail}/${exportJobId}`, {}, {});
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS.FULFILLED, exportJobDetails: applyTransportPlanLegVoyages(res.data) });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS.REJECTED, errors });
        }
    }
};

export const fetchReconciliationPODetails = (referenceId: number, category: number = 0) => {
	const params = { filters: {referenceId, category}};
		return async (dispatch: ThunkDispatch<any, any, Action>) => {
			dispatch({ type: TYPES.FETCH_RECONCILIATION_PO_DETAILS.PENDING });
			try {
				const res = await callApiPost(
					`${API.ExportJob.GetReconciliationPODetails}`,
					params,
					{
						headers: {
							TransportMode:
								TransportMode.Ocean,
						},
					},
				);
				dispatch({
					type: TYPES.FETCH_RECONCILIATION_PO_DETAILS
						.FULFILLED,
                        reconsiliationPODetails: res.data,
				});
			} catch (e) {
				const errors = generateErrors(e);
				dispatch({
					type: TYPES.FETCH_RECONCILIATION_PO_DETAILS.REJECTED,
					errors,
				});
			}
		};
};
// export const fetchReconciliationPODetails = (exportJobId: number) => {
//     return async (dispatch: ThunkDispatch<any, any, Action>) => {
//         dispatch({ type: TYPES.FETCH_RECONCILIATION_PO_DETAILS.PENDING });
//         try {
//             const res = await callApiGet(`${API.ExportJob.GetReconciliationPODetails}/${exportJobId}`, {}, {});
//             dispatch({ type: TYPES.FETCH_RECONCILIATION_PO_DETAILS.FULFILLED, reconsiliationPODetails: res.data });
//         } catch (e) {
//             const errors = generateErrors(e);
//             dispatch({ type: TYPES.FETCH_RECONCILIATION_PO_DETAILS.REJECTED, errors });
//         }
//     }
// };

export const fetchExportJobChargesDetails = (exportJobId: number) => {
    return async (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
        dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_CHARGES.PENDING });
        try {
            const res = isFeatureEnabled(FEATURE_TOGGLES.FINOPS_WEBAPI_EXPORT_JOB_COST_REVENUE_SPLITS, getState().featureToggle.featureToggles)
                ? await callApiV2Get(`${API.ExportJob.ExportJobDetail}/${exportJobId}/cost-revenue-splits`, {}, {})
                : await callApiV3Get(`${API.ExportJob.GetExportJobDetails}/${exportJobId}/cost-revenue`, {}, {});
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_CHARGES.FULFILLED, exportDetailsCostAndRevenue: res.data });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_CHARGES.REJECTED, errors });
        }
    }
};


export const fetchExportJobBookingDetails = (exportJobId: number) => {
    return async (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
        dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_BOOKING.PENDING });
        try {
            const res = isFeatureEnabled(FEATURE_TOGGLES.FINOPS_WEBAPI_EXPORT_JOB_BOOKINGS, getState().featureToggle.featureToggles)
                ? await callApiV2Get(`${API.ExportJob.ExportJobDetail}/${exportJobId}/bookings`, {}, {})
                : await callApiV3Get(`${API.ExportJob.GetExportJobDetails}/${exportJobId}/bookings`, {}, {});
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_BOOKING.FULFILLED, exportDetailsBooking: res.data });
			return res.data;
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_BOOKING.REJECTED, errors });
			throw errors;
        }
    }
};



export const fetchExportJobClrDetails = (exportJobId: number) => {
    return async (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
        dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_CLR.PENDING });
        try {
            const res = isFeatureEnabled(FEATURE_TOGGLES.FINOPS_WEBAPI_EXPORT_JOB_CLR, getState().featureToggle.featureToggles)
                ? await callApiV2Get(`${API.ExportJob.ExportJobsDetail}/${exportJobId}/clr`, {}, {})
                : await callApiV3Get(`${API.ExportJob.GetExportJobDetails}/${exportJobId}/clr`, {}, {});
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_CLR.FULFILLED, exportDetailsClr: res.data });

        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_CLR.REJECTED, errors });
        }
    }
};

export const fetchExportJobCblDetails = (exportJobId: number) => {
    return async (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
        dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_CBL.PENDING });
        try {
            const res = isFeatureEnabled(FEATURE_TOGGLES.FINOPS_WEBAPI_EXPORT_JOB_CBL, getState().featureToggle.featureToggles)
                ? await callApiV2Get(`${API.ExportJob.ExportJobsDetail}/${exportJobId}/cbl`, {}, {})
                : await callApiV3Get(`${API.ExportJob.GetExportJobDetails}/${exportJobId}/cbl`, {}, {});
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_CBL.FULFILLED, exportDetailsCbl: res.data });
        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_CBL.REJECTED, errors });
        }
    }
};

export const fetchExportJobEtsDetails = (exportJobId: number) => {
    return async (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
        dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_ETS.PENDING });
        try {
            const res = isFeatureEnabled(FEATURE_TOGGLES.FINOPS_WEBAPI_EXPORT_JOB_ETS, getState().featureToggle.featureToggles)
                ? await callApiV2Get(`${API.ETS.ExportJobEtsDetails(exportJobId)}`, {}, {})
                : await callApiV3Get(`${API.ExportJob.GetExportJobDetails}/${exportJobId}/ets`, {}, {});
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_ETS.FULFILLED, exportDetailsEts: res.data });

        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_ETS.REJECTED, errors });
        }
    }
};

export const fetchExportJobCustomerInvoice = (exportJobId: number) => {
    return async (dispatch: ThunkDispatch<any, any, Action>) => {
        dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_INVOICE.PENDING });
        try {
            const res = await callApiV3Get(`${API.ExportJob.GetExportJobDetails}/${exportJobId}/customer-invoice`, {}, {});
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_INVOICE.FULFILLED, exportDetailsCustomerInvoice: res.data });

        } catch (e) {
            const errors = generateErrors(e);
            dispatch({ type: TYPES.FETCH_EXPORT_JOB_DETAILS_INVOICE.REJECTED, errors });
        }
    }
};

export const bulkDownloadExportJobs = (documentIds: number[]) => {
    return async (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
      dispatch({ type: TYPES.BULK_DOWNLOAD_EXPORT_JOBS.PENDING })
      try {
        const query: any = { 'ReferenceIds': documentIds, 'UserId': getState().nickname.userNickName.employeeId };
        const res = await callApiPost(`${API.ExportJob.DownloadChargeItems}`, query, { headers: { TransportMode: TransportMode.Ocean } });
        dispatch({ type: TYPES.BULK_DOWNLOAD_EXPORT_JOBS.FULFILLED, bulkDownloadString: res.data })
      } catch (e) {
        const errors = generateErrors(e)
        dispatch({ type: TYPES.BULK_DOWNLOAD_EXPORT_JOBS.REJECTED, errors })
      }
    }
}

export const sendCreateSalesOrderExport = (
    ReferenceId: number,
    DocumentStage: number,
    RateRequestAction: number,
    Category: 'ESSO',
    UserId: string = '',
) => {
    const requestUrl = API.ExportJob.SendCreateSalesOrderExport;
    const requestObject = {
        Filter: [
            {
                ReferenceId,
                DocumentStage,
                RateRequestAction,
                Category,
                UserId,
            },
        ],
    };
    return (dispatch: ThunkDispatch<any, any, Action>, getState: () => IStore) => {
        dispatch({ type: TYPES.SALES_ORDER_EXPORT.PENDING });
        return new Promise((resolve, reject) => {
            callApiV2Post(requestUrl, requestObject)
                .then((res) => {
                    dispatch({
                        type: TYPES.SALES_ORDER_EXPORT.FULFILLED,
                        payload: res.data
                    });
                    resolve(res.data);
                })
                .catch((e) => {
                    dispatch({
                        type: TYPES.SALES_ORDER_EXPORT.REJECTED,
                        payload: 'Error fetching create sales order export',
                    });
                    reject(e);
                });
        });
    };
};
